import React, { useCallback, useContext, useRef } from 'react';
import {
  oneOf,
  bool as boolType,
  number as numberType,
  string as stringType
} from 'prop-types';
import { useDataModel, QueryProvider } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { ProductPodMobile } from '@thd-olt-component-react/product-pod-v6';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import {
  RecommendationsProductPodCarousel
} from '../core/RecommendationsProductPodCarousel';
import { getCustomerData, getSkippers, getAppId } from '../utils/helpers';
import { priceDropDataModel } from '../dataModel';
import { FallbackCard } from '../core/FallbackCard';

const PriceDrop = (props) => {
  const {
    brandTitleMaxLine,
    customerID,
    customerIsLoggedIn,
    hideTitle,
    requestKey,
    showLoading,
    slidesPer,
    showFallback,
    hideATC,
    keepAddToCart,
    noATCFulfillment,
    hideSavingsText,
    hideBulkPrice,
    entryId,
    containerName,
    hideCarouselArrows
  } = props;

  const { isLocalized, storeId, storeZip: zipCode } = useStore();
  const { channel, isConsumerApp } = useContext(ExperienceContext) || {};
  const innerQueryPrerequisites = useRef({ storeId, zipCode });

  const {
    customerType: isLoggedIn, mcvisID, svocID
  } = getCustomerData();

  const custIsLoggedIn = customerIsLoggedIn || isLoggedIn;
  const custID = customerID || (custIsLoggedIn ? svocID : mcvisID);

  const apiName = 'pricedrop';

  const udsQueryOptions = {
    variables: {
      apiName,
      appId: getAppId(channel, isConsumerApp),
      dataSource: apiName,
      key: requestKey,
      storeId,
      userId: custID || '',
      zipCode,
      ...getSkippers({}),
    },
    ssr: false,
  };

  const { loading, data, error, variables } = useDataModel('uds', udsQueryOptions);

  if (data && isLocalized) {
    innerQueryPrerequisites.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
      mounted: true,
    };
  }

  if (error || (!data?.uds?.products?.length && !loading)) {
    if (showFallback) {
      return (
        <FallbackCard
          header="No Updates Right Now"
          subheader="Check back later for savings and offers on your favorite items."
          footerUrl="https://www.homedepot.com/c/localad"
          footer="Shop All Savings"
          componentName="PriceDrop"
        />
      );
    }

    return null;
  }

  const impressionData = {
    id: entryId || '',
    name: containerName || '',
    component: PriceDrop.displayName,
    type: 'product',
    position: '',
    category: ''
  };

  if (data?.uds?.products?.length === 1) {

    const firstProduct = data.uds.products[0];
    const { product } = firstProduct;
    const { itemId } = product;

    return (
      <ImpressionProvider data={impressionData}>
        <div id={apiName} className="uds-price-drop" data-type="container" data-component="PriceDrop">
          <meta data-prop="name" content={apiName} />
          <QueryProvider
            dataSource={apiName}
            cacheKey={`${apiName}-recommendations-carousel`}
            defaultVariables={{
              storeId,
              zipCode,
              installationServices: false,
              ...getSkippers({ skipSubscribeAndSave: true })
            }}
          >
            <ProductPodMobile
              brandTitleMaxLine={4}
              hideATC={hideATC}
              hideBulkPrice={hideBulkPrice}
              hideFavorites
              hideFulfillment
              hideLimitPerOrder
              hidePriceBadge
              hideRating={false}
              hideRebates
              hideSavingsText={false}
              hideShippingInfo
              hideShopThisCollection
              itemId={itemId}
              key={itemId}
              position={0}
              showSubscribeAndSave={false}
              storeId={storeId}
              noATCFulfillment
              podType="horizontal"
              hideAccordion
              hidePromotions
              brand="inline"
            />
          </QueryProvider>
        </div>
      </ImpressionProvider>
    );
  }

  return (
    <ImpressionProvider data={impressionData}>
      <div id={apiName} className="uds-price-drop" data-type="container" data-component="PriceDrop">
        <meta data-prop="name" content={apiName} />
        <RecommendationsProductPodCarousel
          data={data?.uds}
          hideTitle={hideTitle}
          hideATC={hideATC}
          itemClass="fixed-item"
          loading={loading}
          showLoading={showLoading}
          scheme={apiName}
          storeId={storeId}
          brandTitleMaxLine={brandTitleMaxLine}
          zipCode={zipCode}
          slidesPer={slidesPer}
          keepAddToCart={keepAddToCart}
          noATCFulfillment={noATCFulfillment}
          hideSavingsText={hideSavingsText}
          hideBulkPrice={hideBulkPrice}
          hideCarouselArrows={hideCarouselArrows}
        />
      </div>
    </ImpressionProvider>
  );
};

PriceDrop.dataModel = priceDropDataModel;

PriceDrop.displayName = 'PriceDrop';

PriceDrop.propTypes = {
  // product pod takes only these prop values for brandTitleMaxLine
  brandTitleMaxLine: oneOf([2, 3, 4, null]),
  // svocid or mcvisid
  customerID: stringType,
  customerIsLoggedIn: stringType,
  hideTitle: boolType,
  requestKey: stringType.isRequired,
  showLoading: boolType,
  slidesPer: numberType,
  showFallback: boolType,
  hideATC: boolType,
  keepAddToCart: boolType,
  noATCFulfillment: boolType,
  hideSavingsText: boolType,
  hideBulkPrice: boolType,
  entryId: stringType,
  containerName: stringType,
  hideCarouselArrows: boolType
};

PriceDrop.defaultProps = {
  brandTitleMaxLine: 2,
  customerID: null,
  customerIsLoggedIn: null,
  hideTitle: false,
  showLoading: false,
  slidesPer: 2.2,
  showFallback: false,
  hideATC: true,
  keepAddToCart: null,
  noATCFulfillment: null,
  hideSavingsText: false,
  hideBulkPrice: true,
  entryId: null,
  containerName: null,
  hideCarouselArrows: false
};

export { PriceDrop };